import type { ReactNode } from 'react';

import Media from 'react-media';
import loadable from '@loadable/component';

import { useTheme, Modal } from '@simplywallst/ui-core';

import { useUserAgent } from '@simplywallst/hooks/useUserAgent';

import type { Holding } from '@/hooks/portfolio/usePortfolio';
import type { DemoHolding } from '@/hooks/portfolio/useDemoPortfolio';
import type { WatchlistItem } from '@/hooks/useWatchlist';

import { MonitoringPanelHeader } from '../MonitoringPanelHeader';

import {
  MonitoringPanelLayoutMobile,
  MonitoringPanelLayoutTablet,
  MonitoringPanelLayoutDesktop,
} from './styled';

const ANIMATION_DURATION = 300;

const MountTransition = loadable(() => import('../MountTransition'), {
  resolveComponent: (c) => c.MountTransition,
});

interface Props {
  company:
    | WatchlistItem['company']
    | Holding['company']
    | DemoHolding['company']
    | null;
  onClose: () => void;
  children?: ReactNode;
}

const MonitoringPanel = ({ company, onClose, children }: Props) => {
  const theme = useTheme();
  const { isMobile, isTablet, isDesktop } = useUserAgent();

  return (
    <Media
      defaultMatches={{
        mobile: isMobile,
        tablet: isTablet,
        desktop: isDesktop,
      }}
      queries={{
        mobile: { maxWidth: theme.xsMaxRaw },
        tablet: { minWidth: theme.smMinRaw, maxWidth: theme.mdMaxRaw },
        desktop: { minWidth: theme.lgMinRaw },
      }}
    >
      {(match) => {
        if (match.desktop) {
          return (
            <MonitoringPanelLayoutDesktop
              templateRows="min-content auto"
              gap={`${theme.x2spacePx}px`}
            >
              {company ? (
                <>
                  <MonitoringPanelHeader company={company} onClose={onClose} />
                  {children}
                </>
              ) : null}
            </MonitoringPanelLayoutDesktop>
          );
        }

        if (match.tablet && company) {
          return (
            <MountTransition onExited={onClose} duration={ANIMATION_DURATION}>
              {({ state, exit }) => {
                const isOpen = state === 'entering' || state === 'entered';
                return (
                  <Modal isNaked={true}>
                    <MonitoringPanelLayoutTablet
                      isOpen={isOpen}
                      duration={ANIMATION_DURATION}
                      templateRows="min-content auto"
                      gap={`${theme.x2spacePx}px`}
                    >
                      <MonitoringPanelHeader company={company} onClose={exit} />
                      {children}
                    </MonitoringPanelLayoutTablet>
                  </Modal>
                );
              }}
            </MountTransition>
          );
        }

        if (match.mobile && company) {
          return (
            <Modal isNaked={true}>
              <MonitoringPanelLayoutMobile
                templateRows="min-content auto"
                gap={`${theme.x2spacePx}px`}
              >
                <MonitoringPanelHeader company={company} onClose={onClose} />
                {children}
              </MonitoringPanelLayoutMobile>
            </Modal>
          );
        }
        return null;
      }}
    </Media>
  );
};

export default MonitoringPanel;
